<template>
    <div>
        <h5>Owners / Notifications</h5>

        <b-table small borderless responsive sticky-header :items="owners" :fields="fields">
            <template v-slot:cell(email)="row">
                <b-form-input :id="getOwnerId(row.index,'email')" :name="getOwnerId(row.index,'email')"
                              :value="row.item.email" @change="v => row.item.email = v" required/>
            </template>
            <template v-slot:cell(first_name)="row">
                <b-form-input :id="getOwnerId(row.index,'firstName')" :name="getOwnerId(row.index,'firstName')"
                              :value="row.item.firstName" @change="v => row.item.firstName = v" required/>
            </template>
            <template v-slot:cell(last_name)="row">
                <b-form-input :id="getOwnerId(row.index,'lastName')" :name="getOwnerId(row.index,'lastName')"
                              :value="row.item.lastName" @change="v => row.item.lastName = v" required/>
            </template>

            <template v-slot:cell(show_details)="row">
                <b-link @click="row.toggleDetails" class="text-secondary" size="sm">
                    <b-icon :icon="row.detailsShowing ? 'chevron-down' : 'chevron-right'"></b-icon>
                </b-link>
            </template>

            <template v-slot:head(owner)="row">
                Owner
                <b-link :id="getOwnerId(row.index, 'ownerHelp')">
                    <b-icon icon="question-circle" variant="primary"></b-icon>
                </b-link>
                <b-popover :target="getOwnerId(row.index, 'ownerHelp')" triggers="hover" placement="top">
                    Check owner to activate the ownership for this user.
                </b-popover>
            </template>

            <template v-slot:cell(owner)="row">
                <b-form-checkbox :id="getOwnerId(row.index,'owner')"
                                 :name="getOwnerId(row.index,'owner')"
                                 v-model="row.item.owner"/>
            </template>

            <template v-slot:head(notified)="row">
                Notified
                <b-link :id="getOwnerId(row.index, 'notifiedHelp')">
                    <b-icon icon="question-circle" variant="primary"></b-icon>
                </b-link>
                <b-popover :target="getOwnerId(row.index, 'notifiedHelp')" triggers="hover" placement="top">
                    Check notified to activate the notification for this user.
                </b-popover>
            </template>

            <template v-slot:cell(notified)="row">
                <b-form-checkbox :id="getOwnerId(row.index,'notified')"
                                 :name="getOwnerId(row.index,'notified')"
                                 v-model="row.item.notified"/>
            </template>

            <template v-slot:head(edit)>
                <b-link @click="addOwner" class="h4 text-primary">
                    <b-icon icon="plus-circle"></b-icon>
                </b-link>
            </template>
            <template v-slot:cell(edit)="row">
                <b-link @click="removeOwner(row.index)" class="h4 text-danger" v-if="owners.length > 1 || isLeadSpace">
                    <b-icon icon="x-circle"></b-icon>
                </b-link>
            </template>

            <template v-slot:row-details="row">
                <b-card bg-variant="light">
                    <b-form-row>
                        <b-col md="6">
                            <b-form-group>
                                <label :for="getOwnerId(row.index,'ownerCondition')">Owner condition
                                    <b-link :id="getOwnerId(row.index,'ownerConditionHelp')">
                                        <b-icon icon="question-circle" variant="primary"></b-icon>
                                    </b-link>
                                    <b-popover :target="getOwnerId(row.index,'ownerConditionHelp')"
                                               triggers="focus" placement="top">
                                        Leave the condition empty for the default owner.<br>
                                        <b>Syntax : </b><br>
                                        <i>[Field name]<b>=</b>[value]<b>|</b>[other value]</i><br>
                                        Each <b>AND</b> condition separated by ','<br>
                                        <b>Example : </b><i>listValue1=ValueA,listValue2=ValueB|ValueC</i>
                                    </b-popover>
                                </label>
                                <b-form-input :id="getOwnerId(row.index,'ownerCondition')"
                                              :name="getOwnerId(row.index,'ownerCondition')"
                                              :disabled="!row.item.owner"
                                              :value="row.item.ownerCondition"
                                              @change="v => row.item.ownerCondition = v"></b-form-input>
                            </b-form-group>
                        </b-col>
                        <b-col md="6">
                            <b-form-group>
                                <label :for="getOwnerId(row.index,'notificationCondition')">Notification condition
                                    <b-link :id="getOwnerId(row.index,'notificationConditionHelp')">
                                        <b-icon icon="question-circle" variant="primary"></b-icon>
                                    </b-link>
                                    <b-popover :target="getOwnerId(row.index,'notificationConditionHelp')"
                                               triggers="focus" placement="top">
                                        Leave the condition empty so the notification is always sent.<br>
                                        <b>Syntax : </b><br>
                                        <i>[Field name]<b>=</b>[value]<b>|</b>[other value]</i><br>
                                        Each <b>AND</b> condition separated by ','<br>
                                        <b>Example : </b><i>listValue1=ValueA,listValue2=ValueB|ValueC</i>
                                    </b-popover>
                                </label>
                                <b-form-input :id="getOwnerId(row.index,'notificationCondition')"
                                              :name="getOwnerId(row.index,'notificationCondition')"
                                              :disabled="!row.item.notified"
                                              :value="row.item.notificationCondition"
                                              @change="v => row.item.notificationCondition = v"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-form-row>
                </b-card>
            </template>
        </b-table>
    </div>
</template>

<script>
    export default {
        name: "PortalOwners",
        props: {
            owners: Array,
            isLeadSpace: Boolean
        },
        data() {
            return {
                fields: [
                    {key: 'show_details', label: '', class: 'text-center align-middle col-icon'},
                    {key: 'email', class: 'required'},
                    {key: 'first_name', class: 'required'},
                    {key: 'last_name', class: 'required'},
                    {key: 'owner', class: 'text-center align-middle'},
                    {key: 'notified', class: 'text-center align-middle'},
                    {key: 'edit', class: 'text-center align-middle col-icon'}
                ]
            }
        },
        methods: {
            getOwnerId(index, attribute) {
                return 'owner' + index + '-' + attribute;
            },
            addOwner() {
                this.owners.push({owner: true});
            },
            removeOwner(index) {
                this.owners.splice(index, 1)
            }
        }
    }
</script>

<style scoped>

</style>